import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import NoDataFound from "common/component/NoDataFound";
import { openSnackbar } from "../../redux/slices/common";
import { TableLoader } from "./AgGridAccordionChildScrollingTable";

export const defaultColDef = {
  flex: 1,
  minWidth: 100,
  sortable: false,
  lockPinned: true,
  lockPosition: true,
  resizable: false,
};

const defaultPageRow = [10, 50, 100];

const CommonAgGridTable = ({
  columnDefs,
  refreshTable,
  getDataMethod = () => { },
  perPage = 10,
  PageRow = defaultPageRow,
  customFilters = {},
  isSelectedAll = false,
  selectedProducts = [],
  setSelectedProducts,
  selectionSource,
  setSelectionSource,
  tableName = "",
  ...rest
}) => {
  const dispatch = useDispatch()
  const mainGridApiRef = useRef("");

  const [gridApi, setGridApi] = useState(null);
  const [perPageCount, setPerPageCount] = useState(perPage);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const onPaginationChanged = useCallback(() => {
    if (gridApi) {
      const newPageSize = gridApi.paginationGetPageSize();

      if (newPageSize !== perPageCount) {
        setPerPageCount((prev) => {
          if (prev !== newPageSize) {
            return newPageSize;
          }

          return prev;
        });
      }
    }
  }, [gridApi]);

  const handleGetData = useCallback(async (params) => {
    setIsDataLoaded(false)
    try {
      const pageSize = mainGridApiRef?.current?.api?.paginationGetPageSize();
      const page = Math.max(params.endRow, pageSize) / pageSize;
      const sortModel = params?.sortModel;

      if (sortModel.length > 0) {
        const sortedColumn = sortModel[0];
        customFilters.sort = sortedColumn.colId;
        customFilters.order = sortedColumn.sort;
      }
      if (!customFilters?.search) {
        delete customFilters?.search;
      }

      mainGridApiRef?.current?.api?.showLoadingOverlay();
      const request = { ...customFilters, limit: pageSize, page };
      const response = await dispatch(getDataMethod(request)).unwrap();

      const data = tableName === "products" ? response?.data?.products : response?.data || response?.values || [];
      const total = tableName === "products" ? response?.data?.total : response?.total || 0;

      if (data) {
        params.successCallback(data, total);
        mainGridApiRef?.current?.api?.hideOverlay();
        if (!total) {
          mainGridApiRef.current?.api.showNoRowsOverlay();
        }
      } else {
        params.successCallback([], 0);
        mainGridApiRef.current?.api.showNoRowsOverlay();
      }
    } catch (error) {
      const errorMsg = error?.data?.message || error?.message || error
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
    } finally {
      setIsDataLoaded(true)
    }
  }, [customFilters]);

  const handleRowSelected = useCallback((event) => {
    const data = event?.node?.data || {};
    const categoryIndex = selectedProducts.findIndex((row) => row.boxId === data?.boxId);
    if (setSelectionSource) {
      setSelectionSource("child")
    }

    const selectedRows = mainGridApiRef?.current?.api?.getSelectedRows()
    const selections = selectedRows?.map(row => row?.productId)

    if (event.node.isSelected()) {
      setSelectedProducts(prev => {
        let newProducts = [...prev];
        if (categoryIndex >= 0) {
          newProducts[categoryIndex] = { ...newProducts[categoryIndex], productIds: isSelectedAll ? [] : selections, isAllSelected: isSelectedAll };
        } else {
          newProducts = [...newProducts, { boxId: data?.boxId, productIds: isSelectedAll ? [] : selections, isAllSelected: isSelectedAll }];
        }

        return newProducts
      })
    } else {
      setSelectedProducts(prev => {
        const newProducts = [...prev];
        newProducts[categoryIndex] = { ...newProducts[categoryIndex], productIds: selections, isAllSelected: false };

        return newProducts;
      })
    }
  }, [isSelectedAll, selectedProducts]);

  useEffect(() => {
    if (gridApi) {
      const dataSource = { getRows: handleGetData };
      gridApi.setDatasource(dataSource);
    }
  }, [gridApi, refreshTable]);

  useEffect(() => {
    if (!gridApi || selectionSource !== "parent") return;

    const updateSelection = () => {
      if (isSelectedAll) {
        gridApi.forEachNode(node => node.setSelected(true));
      } else {
        const categoryProducts = selectedProducts.find((row) => row.boxId === customFilters?.boxId);

        if (categoryProducts?.productIds?.length > 0) {
          gridApi.forEachNode(node => {
            if (categoryProducts.productIds.includes(node.data?.productId)) {
              node.setSelected(true);
            }
          });
        } else {
          gridApi.deselectAll();
        }
      }
    };

    // Run immediately when dependency changes
    updateSelection();

    // Attach listener for infinite model updates
    gridApi.addEventListener("modelUpdated", updateSelection);

    return () => {
      gridApi.removeEventListener("modelUpdated", updateSelection);
    };

  }, [isSelectedAll, selectionSource, gridApi, selectedProducts, customFilters]);

  return (
    <AgGridReact
      ref={mainGridApiRef}
      className="ag-grid--table"
      pagination={true}
      paginationPageSize={perPageCount}
      cacheBlockSize={perPageCount}
      paginationPageSizeSelector={PageRow}
      onPaginationChanged={onPaginationChanged}
      rowModelType="infinite"
      loadingOverlayComponent={() => <TableLoader />}
      columnDefs={columnDefs}
      noRowsOverlayComponent={() => <NoDataFound />}
      onGridReady={onGridReady}
      headerHeight={36}
      rowHeight={56}
      getRowStyle={(params) => {
        if (params.data === undefined) {
          return { display: "none" };
        }
      }}
      rowSelection="multiple"
      suppressRowClickSelection={true}
      defaultColDef={{ ...defaultColDef }}
      onRowSelected={handleRowSelected}
      {...rest}
    />
  )
}

export default CommonAgGridTable